import { Button, formatNumber, Icon, IconButton, Typography } from '@smartgift/frontend-toolkit';
import React, { useMemo, useState, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import isEmailValid from 'validator/lib/isEmail';

import { Table } from '@hero/molecules';
import { isNameValid } from '@hero/utils';

const Wrapper = styled.div``;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
`;

const SpreadSheetContactTableRow = ({ contacts, updateContacts, isLoading }) => {
  const { t } = useTranslation();
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});

  const pageCount = useMemo(() => {
    return Math.ceil(contacts.length / pageSize);
  }, [contacts.length, pageSize]);

  const contactsByPage = useMemo(() => {
    let pageIndex = 0;
    const result = {};

    while (pageIndex < pageCount) {
      result[pageIndex] = contacts.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
      pageIndex += 1;
    }

    return result;
  }, [contacts, pageSize, pageCount]);

  const columns = React.useMemo(
    () => [
      {
        id: 'email',
        header: t('email'),
        editable: true,
        size: 120,
        accessorKey: 'email',
        isValidFn: val => val && isEmailValid(val),
        errorMessage: t('pleaseEnterAValidEmailAddress'),
        componentProps: {
          maxLength: 100
        }
      },
      {
        header: `${t('name')} (${t('optional')})`,
        errorMessage: t('pleaseEnterAValidName'),
        size: 100,
        accessorKey: 'firstName',
        editable: true,
        isValidFn: val => isNameValid(val),
        componentProps: {
          maxLength: 30
        }
      },
      {
        id: 'actions',
        header: '',
        size: 5,
        cell: ({ table, row }) => {
          const { updateContacts } = table.getState();
          const { editMode } = table.options;
          const { editing } = table.getState();
          const rowIsEditing = editing[row.original.id];

          if (editMode === 'row' && rowIsEditing) {
            return (
              <ActionButtons>
                <IconButton
                  variant="text"
                  color="success"
                  onClick={() => table.options.meta.handleSave(row)}
                >
                  <Icon
                    color="var(--color-components-button-secondary-text-fg)"
                    name="success"
                    size="lg"
                  />
                </IconButton>
                <IconButton
                  variant="text"
                  color="error"
                  onClick={() => table.options.meta.handleCancel(row)}
                >
                  <Icon
                    color="var(--color-components-button-secondary-text-fg)"
                    name="close"
                    size="lg"
                  />
                </IconButton>
              </ActionButtons>
            );
          }

          return (
            <ActionButtons>
              <IconButton
                variant="text"
                color="primary"
                onClick={() => table.options.meta.handleEdit(row)}
              >
                <Icon
                  color="var(--color-components-button-secondary-text-fg)"
                  name="edit"
                  size="lg"
                />
              </IconButton>
              <IconButton
                variant="text"
                color="error"
                onClick={() => {
                  updateContacts(contacts => {
                    return contacts.filter(contact => contact.id !== row.id);
                  });
                }}
              >
                <Icon name="delete" size="lg" />
              </IconButton>
            </ActionButtons>
          );
        }
      }
    ],
    []
  );

  const handleRowUpdate = useCallback(
    updatedRow => {
      updateContacts(contacts => {
        return contacts.map(contact => {
          if (contact.id === updatedRow.id) {
            return {
              ...contact,
              ...updatedRow,
              hasError: !(
                isEmailValid(updatedRow.email) &&
                isNameValid(updatedRow.firstName) &&
                isNameValid(updatedRow.lastName)
              )
            };
          }
          return contact;
        });
      });
    },
    [updateContacts]
  );

  return (
    <Wrapper>
      <Table
        columns={columns}
        data={contactsByPage[pageIndex] || []}
        state={{
          updateContacts,
          globalFilter: globalFilters
        }}
        updateData={handleRowUpdate}
        loading={isLoading}
        setPagination={setPagination}
        globalFilters={globalFilters}
        setGlobalFilters={setGlobalFilters}
        useGlobalFuzzyFilter
        manualPagination
        pagination={{
          pageIndex,
          pageSize,
          total: contacts?.length
        }}
        pageCount={pageCount}
        enabled={{ filter: false, header: false }}
        sectionHeaderProps={{
          enabled: true,
          title: (
            <Typography variant="body2">
              <Trans
                i18nKey="totalContacts"
                values={{ count: formatNumber(contacts?.length) }}
                components={{ b: <b /> }}
              />
            </Typography>
          ),
          cta: (
            <Button onClick={() => {}} variant="link" color="error">
              <Icon name="delete" />
              {t('removeAllAndUploadNew')}
            </Button>
          ),
          searchProps: {
            placeholder: t('searchRecipientsByEmailOrName')
          }
        }}
        editMode="row"
      />
    </Wrapper>
  );
};

export { SpreadSheetContactTableRow };
